import React, { useMemo } from 'react';
import {
	Image,
	ScrollView,
	View,
	StyleSheet,
	TouchableHighlight,
	Platform,
} from 'react-native';
import I18n from '../../../components/shared/i18n/I18n.component';
import colors from '../../../config/colors';
import { toNumber } from 'lodash';
import WorkLog from '../../../components/shared/ClockReport/Record/Record.component';
import { Record } from '../../../components/shared/ClockReport/Record/Record.component';
import ImageIcon from '../../../components/shared/Icon/ImageIcon.component';
import Typography from '../../../components/shared/Typography/Typography.component';
import { i18nService } from '../../../services/i18n.service';
import { useDispatch } from 'react-redux';
import { setOpenChosenDateModal } from '../../../store/actions/clockReport.actions';
import ActionButtonContainer from '../../../components/shared/Buttons/ActionButton.container';
const imageEmptyState = require('../../../../assets/images/reportEmptyState/empty.png');

const convertToHoursFormat = (time: string) => {
	if (!time) return '00:00';
	const [hours, minutes] = time.split(':');
	return `${toNumber(hours).toLocaleString()}:${minutes}`;
};

const convertHoursFormat = (
	minutesValue: any,
	format?: string,
	inminutes?: boolean,
) => {
	if (minutesValue) {
		const value = inminutes ? minutesValue : Math.floor(minutesValue * 60);
		if (format === 'HH:mm') {
			const hours = `${Math.floor(value / 60)?.toLocaleString()}`;
			const minutes =
				value % 60 !== 0
					? value % 60 < 10
						? `0${value % 60}`
						: `${value % 60}`
					: '00';
			return `${hours}:${minutes}`;
		}
		return `${(value / 60).toFixed(2)}`;
	}
	return '00:00';
};

interface Props {
	reportSelectedDate: any[];
	openDrawer: any;
	onDelete: Function;
	onEdit: Function;
	hideButton: boolean;
	addWorkLog: Function;
	standardHours: any;
	totalReported: any;
	isToday: boolean;
	selectedDay: any;
	navigation: any;
	canEdit: boolean;
}

const DayReportsMobile = ({
	onDelete,
	onEdit,
	hideButton,
	addWorkLog,
	standardHours,
	totalReported,
	isToday,
	selectedDay,
	reportSelectedDate,
	navigation,
	canEdit,
}: Props) => {
	const dispatch = useDispatch();

	const standard = useMemo(() => {
		return convertToHoursFormat(standardHours?.fixHours);
	}, [standardHours]);

	return (
		<View style={styles.container}>
			{/* Back button */}
			<View style={styles.backRow}>
				<TouchableHighlight
					style={styles.backButton}
					underlayColor='transparent'
					onPress={() => {
						dispatch(setOpenChosenDateModal(false));
						navigation.pop();
					}}
				>
					<View style={styles.backButtonInner}>
						<I18n>reportView.tableReports</I18n>
						<ImageIcon name={'leftArrow'} width={10} height={10} style={{transform: [{ rotate: '180deg' }], marginLeft: 8}}/>

					</View>
				</TouchableHighlight>
			</View>

			{/* White card */}
			<View style={styles.card}>
				{/* Header: title + date on right, hours on left */}
				<View style={styles.detailsHeader}>
					<View style={styles.hoursRow}>
						<Typography size={14} color='clockRecordBlue' font='medium'>
							{convertHoursFormat(totalReported, 'HH:mm', false)}
						</Typography>
						<Typography size={14} color='greyText' weight='normal'>
							{`/${standard}`}
						</Typography>
					</View>
					<View style={styles.titleRow}>
						<Typography size={14} color='greyText' weight='normal' style={styles.titleRowDate}>
							{selectedDay}
						</Typography>
						<Typography size={14} color='primaryDarkBlue' font='medium'>
							{i18nService.translate('reportView.reportFrom')}
						</Typography>
					</View>
				</View>

				{/* Records or empty state */}
				{reportSelectedDate?.length ? (
					<View style={styles.tableContainer}>
						<ScrollView style={styles.tableHolder}>
							{reportSelectedDate?.map((record: Record) => (
								<WorkLog
									onEdit={onEdit}
									onDelete={onDelete}
									isToday={isToday}
									key={record.workLogId}
									record={record}
								/>
							))}
						</ScrollView>
					</View>
				) : (
					<View style={styles.emptyState}>
						<Image
							source={imageEmptyState}
							fadeDuration={0}
							style={styles.emptyImage}
						/>
						<I18n size={14} color='greyText' weight='400'>
							{isToday
								? 'reportView.noWorklogsToday'
								: 'general.noLogsForThisDate'}
						</I18n>
					</View>
				)}
			</View>

			{/* Bottom add button */}
			{!hideButton && canEdit && (
				<ActionButtonContainer 
					onPress={() => addWorkLog()}
					overrideStyle={styles.addButton}
				>
					<View style={styles.addButtonInner}>
				 		<I18n size={16} weight='normal' color='white' style={styles.addButtonText} hoverStyle={{backgroundColor: colors.primaryDark}}>
				 			general.add
				 		</I18n>
				 		<ImageIcon name='plusWhite' height={18} width={18} />
				 	</View>
				</ActionButtonContainer>
			)}
		</View>
	);
};

const styles = StyleSheet.create({
	container: {
		flex: 1,
	},
	backRow: {
		flexDirection: 'row-reverse',
		paddingTop: 20,
		paddingBottom: 10,
		paddingHorizontal: 15,
	},
	backButton: {
		flexDirection: 'row-reverse',
		width: 100,
	},
	backButtonInner: {
		flexDirection: 'row',
		justifyContent: 'space-between',
		alignItems: 'center',
	},
	card: {
		flex: 1,
		margin: 16,
		backgroundColor: colors.white,
		borderRadius: 8,
		padding: 20,
		...Platform.select({
			web: { boxShadow: '2px 2px 16px 0 rgba(0, 0, 0, 0.04)' },
			ios: { shadowColor: '#000', shadowOffset: { width: 2, height: 2 }, shadowOpacity: 0.04, shadowRadius: 16 },
			android: { elevation: 2 },
		}),
	},
	detailsHeader: {
		flexDirection: 'row',
		justifyContent: 'space-between',
		alignItems: 'center',
		marginBottom: 16,
	},
	hoursRow: {
		flexDirection: 'row',
		alignItems: 'center',
	},
	titleRow: {
		flexDirection: 'row',
		alignItems: 'center',
		...Platform.select({ web: { gap: 8 } }),
	},
	titleRowDate: {
		...Platform.select({ ios: { marginRight: 8 }, android: { marginRight: 8 } }),
	},
	tableContainer: {
		flex: 1,
	},
	columnHeaderRow: {
		flexDirection: 'row',
		alignItems: 'center',
		paddingVertical: 6,
		borderBottomWidth: 1,
		borderBottomColor: '#E0E0E0',
	},
	columnHeader: {
		flex: 1,
		alignItems: 'center',
	},
	tableHolder: {
		flex: 1,
	},
	emptyState: {
		flex: 1,
		justifyContent: 'center',
		alignItems: 'center',
	},
	emptyImage: {
		height: 100,
		width: 100,
		marginBottom: 12,
	},
	addButton: {
		backgroundColor: colors.primary,
		margin: 14,
		marginTop: 0,
		paddingVertical: 11,
		paddingHorizontal: 12,
	},
	addButtonInner: {
		flexDirection: 'row',
		justifyContent: 'center',
		alignItems: 'center',
		...Platform.select({ web: { gap: 8 } }),
	},
	addButtonText: {
		...Platform.select({ ios: { marginRight: 8 }, android: { marginRight: 8 } }),
	},
});

export default DayReportsMobile;
